import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { userService } from '../services/userService';
import { Button, Input, Heading, Text } from './ui';
import '../styles/Login.css';

const LockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0110 0v4"></path>
  </svg>
);

function Waitlist() {
  const navigate = useNavigate();
  const [previewCode, setPreviewCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = authService.getCurrentUser();
      if (!user) {
        setError('Please sign in again to continue.');
        return;
      }

      const result = await userService.validatePreviewCode(previewCode);

      if (result.valid) {
        // redeemPreviewCode performs the grant (hasPreviewAccess +
        // clearing joinedWaitlistAt) after re-validating the code itself.
        // The client no longer writes those fields — granting is server-owned.
        let granted = false;
        try {
          const redemption = await userService.redeemPreviewCode(previewCode);
          granted = redemption.valid;
        } catch (error) {
          console.error('Error redeeming preview code:', error);
        }

        if (!granted) {
          setError(
            "We couldn't apply that code. Please try again or request a new one."
          );
          setLoading(false);
          return;
        }

        navigate('/');
      } else {
        setError(
          result.retired
            ? 'This code is no longer active. Please request a new one.'
            : 'Invalid preview code. Please try again.'
        );
        // Add shake animation
        const input = document.getElementById('waitlistPreviewCode');
        input.classList.add('shake');
        setTimeout(() => input.classList.remove('shake'), 500);
      }
    } catch (error) {
      console.error('Error validating preview code:', error);
      setError('Unable to validate code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-container">
          <img src="/FullLogo.png" alt="Housfully" className="logo" />
        </div>
        <Heading level={1} tone="dark" className="preview-title">
          You're on the waitlist!
        </Heading>
        <Text tone="dark" className="subtitle">
          Thank you for your interest in Housfully. We'll notify you by email
          when your access is ready.
        </Text>

        {!showCodeInput ? (
          <>
            <button
              type="button"
              className="waitlist-button"
              onClick={() => setShowCodeInput(true)}
            >
              I have a preview code
            </button>
            <button
              type="button"
              className="sign-out-button"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmitCode} className="login-form">
              <Input
                tone="dark"
                type="text"
                id="waitlistPreviewCode"
                value={previewCode}
                onChange={(e) => setPreviewCode(e.target.value.toUpperCase())}
                placeholder="Enter preview code"
                disabled={loading}
                autoComplete="off"
                style={{ textTransform: 'uppercase' }}
                leadingIcon={LockIcon}
              />

              <Button
                type="submit"
                tone="dark"
                variant="primary"
                fullWidth
                loading={loading}
                disabled={loading || !previewCode}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
            </form>

            <button
              type="button"
              className="text-button"
              onClick={() => {
                setShowCodeInput(false);
                setPreviewCode('');
                setError('');
              }}
              disabled={loading}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Waitlist;
